<script setup lang="ts">
// An input's pictures (DetailPanel): the source as it arrives and what the
// encodes get, live (MJPEG, streamed only while shown) or a still every
// 2 s, and the lossless frame grab. The pictures share the panel's width
// (two columns that scale): two fixed 176 px pictures overflowed the 400 px
// panel once it showed a scrollbar, and the second one wrapped.
import { computed } from 'vue'
import { api, type Encode, type Input, type InputStatus } from '../../api'
import { pageHidden, previewLive, vStream } from '../../preview'
import { useNode } from '../../store'
import PreviewToggle from './PreviewToggle.vue'

const props = defineProps<{ input: Input; snap: InputStatus; passedBy?: Encode | { name: string } }>()
const node = useNode()
// thumbnail refreshes every 2 s (capture writes one every ~2 s)
const thumbTick = computed(() => Math.floor(node.now / 2000))
// degraded is laid over running (a warning, its ring still filling): a caster
// or a multiview short of CPU lost its picture and its grab with it
const running = computed(() => ['running', 'no_signal', 'black', 'degraded'].includes(props.snap.state))
const openFrame = (id: string) => window.open(api.framePng(id), '_blank')
</script>

<template>
    <div class="mt-3">
        <div class="grid grid-cols-2 gap-3">
            <!-- the source as it arrives (black without a signal) and what the encodes get (slate, overlay, fallback on top) -->
            <figure v-if="passedBy" class="m-0 min-w-0">
                <!-- not decoded by a capture (skip decoding, or a player): live, the node decodes its receiver's stream while shown;
                     else the receiver's key-frame thumbnail -->
                <img
                    v-if="previewLive && !pageHidden && running"
                    :key="'live-' + input.id"
                    v-stream
                    class="block aspect-video w-full rounded bg-bg object-contain"
                    :src="api.preview(input.id)"
                    alt=""
                    @error="($event.target as HTMLImageElement).style.visibility = 'hidden'"
                />
                <img
                    v-else
                    class="block aspect-video w-full rounded bg-bg object-contain"
                    :src="api.thumb(input.id, thumbTick)"
                    alt=""
                    @error="($event.target as HTMLImageElement).style.visibility = 'hidden'"
                    @load="($event.target as HTMLImageElement).style.visibility = 'visible'"
                />
                <figcaption class="mt-0.5 text-xs text-fg-3">
                    source · {{ previewLive && !pageHidden ? 'live' : 'key frames, every few seconds' }}
                </figcaption>
            </figure>
            <figure v-for="view in passedBy ? [] : (['source', 'output'] as const)" :key="view" class="m-0 min-w-0">
                <img
                    v-if="previewLive && !pageHidden && running"
                    :key="'live-' + input.id + view"
                    v-stream
                    class="block aspect-video w-full rounded bg-bg object-contain"
                    :src="api.preview(input.id, view)"
                    :alt="view"
                    @error="($event.target as HTMLImageElement).style.visibility = 'hidden'"
                />
                <img
                    v-else
                    class="block aspect-video w-full rounded bg-bg object-contain"
                    :src="api.thumb(input.id, thumbTick, view)"
                    alt=""
                    @error="($event.target as HTMLImageElement).style.visibility = 'hidden'"
                    @load="($event.target as HTMLImageElement).style.visibility = 'visible'"
                />
                <figcaption class="mt-0.5 truncate text-xs text-fg-3">
                    {{ view === 'source' ? 'source' : 'output · with slate, overlay' }}
                </figcaption>
            </figure>
        </div>
        <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <PreviewToggle />
            <!-- not decoded (skip decoding): the node decodes its receiver's newest key frame instead -->
            <template v-if="node.config?.mode !== 'player'">
                <button
                    class="text-xs"
                    :disabled="!running"
                    :title="
                        passedBy
                            ? 'Full size, its newest key frame, from the stream it receives'
                            : 'Full size, lossless, from the live ring'
                    "
                    @click="openFrame(input.id)"
                >
                    Grab frame (PNG)
                </button>
            </template>
        </div>
    </div>
</template>
